import React, { FC, useEffect, useMemo, useState } from 'react';

import useLocales from 'hooks/useLocales';
import { JobService } from 'services/jobs/job';
import { RequestsService } from 'services/requests/request';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import RecruiterIncomingRequests from 'components/shared/RecruiterIncomingRequests';
import Popup from 'components/shared/Popups/Popup/Index';
import ProfilePopUp from 'components/shared/ProfilePopUp/Index';
import {
  CircularProgress,
  TextField,
  Pagination
} from '@material-ui/core';
import Image from 'components/image/Image';
import OutgoingRequests from 'components/shared/OutgoingRequests/Index';
import Navbar from 'components/Navbar';
import MatchResultHeader from 'components/matchResultHeader';
import { useLocation, useParams } from 'react-router';
import Footer from 'components/Footer/Index';
import NoData from 'components/shared/NoData/Index';
import { STAGE_TYPES, STATUS_TYPES } from 'core/constants/jobCardConstant';
import { useDebounce } from 'helpers/useDebounce';
import OrderBySelect from 'components/shared/OrderBySelect/Index';
import {
  DEFAULT_ORDERING,
  ScoreOrdering
} from 'core/constants/ordering';

import './Index.scss';

const FindCandidate: FC = (): JSX.Element => {
  const { t } = useLocales();
  const { applications } = useSelector(
    (state: RootState) => state.applications
  );
  const [openModal, setOpenModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<any>(null);

  const { singleRequest }: any = useSelector(
    (state: RootState) => state.requests
  );
  const { id } = useParams();
  const location = useLocation();
  const { singleJob: job } = useSelector((state: RootState) => state.jobs);
  const [filteredApplications, setFilteredApplications] = useState<any>([]);
  const [lastFilterType, setLastFilterType] = useState<number | null>();
  const [filteredJobs, setFilteredJobs] = useState<any>([]);
  const [lastFilterStatus, setLastFilterStatus] = useState<number | null>();

  const [statusCounts, setStatusCounts] = useState<any>({
    0: 0,
    1: 0,
    2: 0,
    3: 0,
    4: 0
  });

  const [searchQuery, setSearchQuery] = useState<string>('');
  const [outgoingSearchQuery, setOutgoingSearchQuery] = useState<string>('');
  const [ordering, setOrdering] = useState<ScoreOrdering>(DEFAULT_ORDERING);
  const [outgoingOrdering, setOutgoingOrdering] =
    useState<ScoreOrdering>(DEFAULT_ORDERING);

  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const debouncedOutgoingSearchQuery = useDebounce(outgoingSearchQuery, 500);

  const getJobById = () => {
    JobService.getJobByIdOrSlug(id ? +id : 0);
  };

  useEffect(() => {
    getJobById();
  }, [id]);

  const stageCounts = useMemo(() => {
    const counts: Record<number, number> = {
      0: 0,
      1: 0,
      2: 0,
      3: 0,
      4: 0,
      5: 0
    };
    applications?.results?.forEach((el: { stage: number }) => {
      if (el.stage in counts) {
        counts[el.stage] += 1;
      }
    });
    return counts;
  }, [applications]);

  const handleModal = (open: boolean, requestId: number) => {
    setOpenModal(open);

    let element = applications?.results?.find((el: any) => el.id === requestId);

    if (!element) {
      element = singleRequest?.results?.find((el: any) => el.id === requestId);
    }

    if (!element) {
      return;
    }

    setSelectedRequest(element);
  };

  const handleClosePopup = () => {
    setOpenModal(false);
    setSelectedRequest(null);
  };

  useEffect(() => {
    setFilteredApplications(applications?.results || []);
    setLastFilterType(null);
  }, [applications]);

  useEffect(() => {
    const newStatusCounts: any = { ...statusCounts };
    Object.keys(newStatusCounts).forEach((key) => {
      newStatusCounts[key] = 0;
    });
    singleRequest?.results?.forEach((result: { status: number }) => {
      const status = result?.status;
      if (status in newStatusCounts) {
        newStatusCounts[status]++;
      }
    });
    setStatusCounts(newStatusCounts);
    setLastFilterStatus(null);
    setFilteredJobs(singleRequest?.results);
  }, [singleRequest]);

  const changeStage = async (requestId: number, data: any) => {
    await JobService.manageJob(requestId, data);
    getApplications();
  };

  const [page, setPage] = useState(1);
  const [limit] = useState(3);
  const [offset, setOffset] = useState(0);
  const [outgoingPage, setOutgoingPage] = useState(1);
  const [outgoingLimit] = useState(3);
  const [outgoingOffset, setOutgoingOffset] = useState(0);

  useEffect(() => {
    setOffset((page - 1) * limit);
  }, [page, limit]);

  useEffect(() => {
    setOutgoingOffset((outgoingPage - 1) * outgoingLimit);
  }, [outgoingPage, outgoingLimit]);

  const getApplications = (): void => {
    JobService.getApplications(
      id ? +id : 0,
      debouncedSearchQuery,
      limit,
      offset,
      ordering
    );
  };

  const getCandidateRequests = (query?: string): void => {
    if (!id) {
      return;
    }

    RequestsService.getRequest(
      +id,
      query,
      outgoingLimit,
      outgoingOffset,
      outgoingOrdering
    );
  };

  useEffect(() => {
    getApplications();
  }, [debouncedSearchQuery, limit, offset, id, location.key, ordering]);

  useEffect(() => {
    getCandidateRequests(debouncedOutgoingSearchQuery);
  }, [
    debouncedOutgoingSearchQuery,
    outgoingLimit,
    outgoingOffset,
    id,
    location.key,
    outgoingOrdering
  ]);

  const deleteHandler = (applicationId: number) => {
    JobService.manageJob(applicationId, { status: 5 }).then(() => {
      getApplications();
    });
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const handleOutgoingSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setOutgoingSearchQuery(event.target.value);
  };

  const stageOutgoingHandler = (requestId: number, data: any) => {
    RequestsService.changeRequest(data, requestId, 'find-candidate');
  };

  const handleStagefilter = (type: number) => {
    if (lastFilterType === type) {
      setFilteredApplications(applications?.results || []);
      setLastFilterType(null);
      return;
    }
    setFilteredApplications(
      applications?.results?.filter((app: any) => app.stage === type) || []
    );
    setLastFilterType(type);
  };

  const handleStatusFilter = (type: number) => {
    if (lastFilterStatus === type) {
      setFilteredJobs(singleRequest?.results);
      setLastFilterStatus(null);
      return;
    }
    setFilteredJobs(
      singleRequest?.results?.filter(
        (app: { status: number }) => app.status === type
      )
    );
    setLastFilterStatus(type);
  };

  const handleSetOpen = (requestId: number, _type?: string) => {
    handleModal(true, requestId);
  };

  const handleAccept = async (applicationId: number) => {
    await JobService.acceptApplication(applicationId);
    getApplications();
  };

  const handleDecline = async (applicationId: number) => {
    await JobService.denyApplication(applicationId);
    getApplications();
  };

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchQuery, id]);

  useEffect(() => {
    setOutgoingPage(1);
  }, [debouncedOutgoingSearchQuery, id]);

  const handleOrderingChange = (value: ScoreOrdering): void => {
    setOrdering(value);
    setPage(1);
    setOffset(0);
  };

  const handleOutgoingOrderingChange = (value: ScoreOrdering): void => {
    setOutgoingOrdering(value);
    setOutgoingPage(1);
    setOutgoingOffset(0);
  };

  return (
    <div className="findCandidatePage">
      <div className="findCandidate">
        <Navbar />
        <MatchResultHeader data={job} />
        <div className="candidateOver">
          <div className="candidateOver__right">
            <div className="candidateOver__right__top">
              <div className="candidateOver__right__top-header">
                <span className="candidateOver__right__top-title">
                  {t('Incoming requests')}
                </span>
                <OrderBySelect
                  value={ordering}
                  onChange={handleOrderingChange}
                />
              </div>
              <div className="candidateOver__right__top__counts">
                {STAGE_TYPES.map((stageType) => (
                  <button
                    key={stageType.value}
                    className={`candidateOver__right__top__counts-pass btn${
                      lastFilterType === stageType.value ? ' active' : ''
                    }`}
                    onClick={() => handleStagefilter(stageType.value)}
                  >
                    {lastFilterType === stageType.value && (
                      <Image name="check.svg" />
                    )}
                    {t(stageType.to_show)} ({stageCounts[stageType.value]})
                  </button>
                ))}
              </div>
              <div className="search-container">
                <TextField
                  value={searchQuery}
                  onChange={handleSearch}
                  className="dropdown-search"
                  placeholder="Search"
                  variant="outlined"
                />
              </div>
            </div>
            <div className="candidateOver__right__bottom">
              {applications === null ? (
                <div className="loadingContainer">
                  <CircularProgress className="loading" />
                </div>
              ) : filteredApplications.length ? (
                filteredApplications?.map((item: any) => (
                  <RecruiterIncomingRequests
                    key={item.id}
                    id={item.id}
                    name={item.candidate.name}
                    email={item.candidate.email}
                    sub_title={item.candidate.pitch}
                    icon={item.candidate.avatar}
                    score={item.score}
                    culture_score={item.culture_score}
                    date={item.created_at}
                    history={item.history}
                    industry={item.candidate.industries}
                    stage={item.stage}
                    status={item.status}
                    handleCancel={deleteHandler}
                    handleOpenModal={handleSetOpen}
                    handleAccept={handleAccept}
                    handleDecline={handleDecline}
                    onDelete={deleteHandler}
                    showDelete
                    onStageChange={changeStage}
                    candidateId={item.candidate?.id}
                    jobTitle={item?.job?.title || ''}
                  />
                ))
              ) : (
                <div className="no_data">
                  <NoData />
                </div>
              )}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginTop: 16
                }}
              >
                <Pagination
                  className="pagination"
                  count={Math.ceil((applications?.count || 0) / limit)}
                  page={page}
                  onChange={(_, value) => setPage(value)}
                  variant="outlined"
                  shape="rounded"
                  showFirstButton
                  showLastButton
                />
              </div>
            </div>
          </div>

          <div className="candidateOver__right">
            <div className="candidateOver__right__top">
              <div className="candidateOver__right__top-header">
                <span className="candidateOver__right__top-title">
                  {t('Outgoing requests')}
                </span>
                <OrderBySelect
                  value={outgoingOrdering}
                  onChange={handleOutgoingOrderingChange}
                />
              </div>
              <div className="candidateOver__right__top__counts">
                {STATUS_TYPES?.map((statusType: any) => (
                  <button
                    onClick={() => handleStatusFilter(statusType.value)}
                    key={statusType.value}
                    className="candidateOver__right__top__counts-pass btn"
                  >
                    {lastFilterStatus === statusType.value && (
                      <Image name="check.svg" />
                    )}
                    {t(statusType.label)} ({statusCounts[statusType.value]})
                  </button>
                ))}
              </div>
              <div className="search-container">
                <TextField
                  value={outgoingSearchQuery}
                  onChange={handleOutgoingSearch}
                  className="dropdown-search"
                  placeholder="Search"
                  variant="outlined"
                />
              </div>
            </div>
            <div className="candidateOver__right__bottom">
              {singleRequest === null ? (
                <div className="loadingContainer">
                  <CircularProgress className="loading" />
                </div>
              ) : filteredJobs?.length ? (
                filteredJobs?.map((item: any) => (
                  <OutgoingRequests
                    key={item.id}
                    id={item.id}
                    name={item.candidate.name}
                    email={item.candidate.email}
                    sub_title={item.candidate.pitch}
                    icon={item.candidate.avatar}
                    score={item.score}
                    culture_score={item.culture_score}
                    date={item.created_at}
                    industry={item.candidate.industries}
                    stage={item.stage}
                    history={item.history}
                    status={item.status}
                    stageOutgoingHandler={stageOutgoingHandler}
                    setOpen={() => handleModal(true, item?.id)}
                    jobTitle={
                      item?.origin_job?.title ||
                      item?.origin_profile?.title ||
                      ''
                    }
                  />
                ))
              ) : (
                <div className="no_data">
                  <NoData />
                </div>
              )}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginTop: 16
                }}
              >
                <Pagination
                  className="pagination"
                  count={Math.ceil((singleRequest?.count || 0) / outgoingLimit)}
                  page={outgoingPage}
                  onChange={(_, value) => setOutgoingPage(value)}
                  variant="outlined"
                  shape="rounded"
                  showFirstButton
                  showLastButton
                />
              </div>
            </div>
          </div>
        </div>
        {openModal && selectedRequest && (
          <Popup
            className="popUp"
            title={t('Match Profile')}
            description={selectedRequest?.job?.title}
            onClose={handleClosePopup}
          >
            <ProfilePopUp
              selectedRequest={selectedRequest}
              getJob={
                selectedRequest?.job ||
                selectedRequest?.origin_job ||
                selectedRequest?.origin_profile
              }
              id={selectedRequest?.id}
              onClick={() =>
                changeStage(selectedRequest?.id, {
                  status: 4
                })
              }
              status={selectedRequest.status}
              isCandidate={false}
            />
          </Popup>
        )}
      </div>
      <div className="footers">
        <Footer />
      </div>
    </div>
  );
};

export default FindCandidate;
